import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _regeneratorRuntime from "@babel/runtime/regenerator";
import { useEffect, useRef, useState } from 'react';
import { initializePreferencesData } from '../../../../controllers/cookie-controls/transformer';
import { getPreferences } from '../../../../controllers/preferences-controls//get-preferences';
import { updatePreferences } from '../../../../controllers/preferences-controls//update-preferences';
/**
 * This function is responsible for loading the user's preferences from storage, and setting the state accordingly.
 * If the user's preferences are not found in storage, the default preferences will be used.
 */
var loadStoredPreferences = /*#__PURE__*/function () {
  var _ref2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(_ref) {
    var setPreferencesData, isInitialRender, prefs;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) switch (_context.prev = _context.next) {
        case 0:
          setPreferencesData = _ref.setPreferencesData, isInitialRender = _ref.isInitialRender;
          _context.next = 3;
          return getPreferences();
        case 3:
          prefs = _context.sent;
          // If something has gone wrong during this `get` process (invalid token, failed request, etc.)
          // these preferences will be undefined and we'll need to show the banner as we're in an unknown state
          setPreferencesData({
            prefs: prefs,
            isPrefetchedPrefsInternalState: Boolean(prefs)
          });

          // Set the initialRender flag to false so we can skip logic in banner rendering
          if (isInitialRender) {
            isInitialRender.current = false;
          }
        case 6:
        case "end":
          return _context.stop();
      }
    }, _callee);
  }));
  return function loadStoredPreferences(_x) {
    return _ref2.apply(this, arguments);
  };
}();

/**
 * This hook is responsible for handling any initially provided preference data, and/or fetching missing preference
 * data from ConsentHub. It will then set the appropriate cookies and state for the user's preferences to be used
 * throughout the application.

 * @param (optional) initialPreferences - Any initially provided preferences from consuming applications (used for SSR)
 * @returns (object) {
 * 	preferences - The current state of the user's preferences after fetching/transforming/setting cookies
 *	isPrefetchedPreferencesState - A boolean that indicates if we are in a prefetched internal state
 *	setPreferencesData - Setter function to update the preferences state
 *	isInitialRender - A boolean that indicates if this is the first render of the component
 * }
 */
var usePreferences = function usePreferences(_ref3) {
  var initialPreferences = _ref3.initialPreferences;
  /**
   * isInitialRender is used to ensure consistent banner visibility by addressing potential mismatches that occur
   *  during the initial render. Without this flag, the application might incorrectly display a banner based on
   * incomplete or missing preference data. During the rehydration process, this initial render triggers
   * the useMemo and useEffect hooks which means that initialPreferences may be undefined due to the asynchronous
   * nature of setting props. This can lead to discrepancies in banner visibility between the SSR state
   * and the client-rendered state.
   */
  var isInitialRender = useRef(true);
  var prefetchedPreferencesData = useRef(initialPreferences);
  var _useState = useState(initializePreferencesData(initialPreferences)),
    _useState2 = _slicedToArray(_useState, 2),
    preferencesData = _useState2[0],
    setPreferencesData = _useState2[1];
  useEffect(function () {
    var _initializePreference = initializePreferencesData(prefetchedPreferencesData.current),
      unpackedPrefetchedPrefs = _initializePreference.prefs,
      isPrefetchedPrefsInternalState = _initializePreference.isPrefetchedPrefsInternalState;
    if (!isPrefetchedPrefsInternalState && unpackedPrefetchedPrefs) {
      // With a valid set of prefs, set them on the cookies, but don't save to ConsentHub
      updatePreferences(unpackedPrefetchedPrefs, undefined, true);
    }
    // On initial render, this will either set the proper preferences in state + required cookies based on the
    // prefetched data, or fetch all of that data from ConsentHub and then set appropriate cookies + state.
    loadStoredPreferences({
      setPreferencesData: setPreferencesData,
      isInitialRender: isInitialRender
    });
  }, [isInitialRender]);
  return {
    preferences: preferencesData === null || preferencesData === void 0 ? void 0 : preferencesData.prefs,
    isPrefetchedPreferencesState: preferencesData === null || preferencesData === void 0 ? void 0 : preferencesData.isPrefetchedPrefsInternalState,
    setPreferencesData: setPreferencesData,
    isInitialRender: isInitialRender.current
  };
};
export default usePreferences;